import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from 'common/ui/pagination';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

export type PagingProps = {
  count: number,
  page: number,
  page_count: number,
  page_size: number,
};

function DataTablePagination({ paging }: { paging: PagingProps }) {
  return (
    <Pagination>
      <PaginationContent>{renderPaginationItems(paging)}</PaginationContent>
    </Pagination>
  );
}

export default DataTablePagination;

const renderPaginationItems = paging => {
  const { page, page_count } = paging;
  const pagesToShow = 5; // Number of pages to display in pagination
  const startPage = Math.max(1, page - Math.floor(pagesToShow / 2));
  const endPage = Math.min(page_count, startPage + pagesToShow - 1);

  const [searchParams, setSearchParams] = useSearchParams();
  const items = [];

  // Add "First" button
  items.push(
    <PaginationItem key="first">
      <PaginationButton
        isDisabled={page <= 1}
        onClick={e => {
          e.preventDefault();
          if (page > 1) {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: '1',
            });
          }
        }}
      >
        <ChevronsLeft />
      </PaginationButton>
    </PaginationItem>
  );

  // Add "Previous" button
  items.push(
    <PaginationItem key="prev">
      <PaginationPrevious
        isDisabled={page <= 1}
        onClick={e => {
          e.preventDefault();
          if (page > 1) {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: (page - 1).toString(),
            });
          }
        }}
      />
    </PaginationItem>
  );

  // Add first page if not in range
  if (startPage > 1) {
    items.push(
      <PaginationItem key={1}>
        <PaginationButton
          onClick={e => {
            e.preventDefault();
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: '1',
            });
          }}
        >
          1
        </PaginationButton>
      </PaginationItem>
    );

    // Add ellipsis if there's a gap
    if (startPage > 2) {
      items.push(
        <PaginationItem key="ellipsis-start">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
  }

  // Add page numbers
  for (let i = startPage; i <= endPage; i++) {
    items.push(
      <PaginationItem key={i}>
        <PaginationButton
          isActive={i === page}
          onClick={e => {
            e.preventDefault();
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: i.toString(),
            });
          }}
        >
          {i}
        </PaginationButton>
      </PaginationItem>
    );
  }

  // Add ellipsis and last page if there are more pages
  if (endPage < page_count) {
    // Add ellipsis if there's a gap
    if (endPage < page_count - 1) {
      items.push(
        <PaginationItem key="ellipsis-end">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Add last page
    items.push(
      <PaginationItem key={page_count}>
        <PaginationButton
          onClick={e => {
            e.preventDefault();
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: page_count.toString(),
            });
          }}
        >
          {page_count}
        </PaginationButton>
      </PaginationItem>
    );
  }

  // Add "Next" button
  items.push(
    <PaginationItem key="next">
      <PaginationNext
        isDisabled={page >= page_count}
        onClick={e => {
          e.preventDefault();
          if (page < page_count) {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: (page + 1).toString(),
            });
          }
        }}
      />
    </PaginationItem>
  );

  // Add "Last" button
  items.push(
    <PaginationItem key="last">
      <PaginationButton
        isDisabled={page >= page_count}
        onClick={e => {
          e.preventDefault();
          if (page < page_count) {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: page_count.toString(),
            });
          }
        }}
      >
        <ChevronsRight />
      </PaginationButton>
    </PaginationItem>
  );

  return items;
};
